import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { countryCodeToFlag } from '../../../utils/countryFlag';
import ClearIcon from '@material-ui/icons/Clear';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { UserEntity } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  root: { position: 'relative' },
  memberList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    padding: 0,
  },
  memberItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  emptyState: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.875rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  searchBox: { position: 'relative' },
  dropdown: {
    position: 'absolute',
    zIndex: 1300,
    width: '100%',
    maxHeight: 260,
    overflow: 'auto',
  },
  dropdownItem: {
    cursor: 'pointer',
    '&:hover': { background: theme.palette.action.hover },
  },
  secondary: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  removeBtn: {
    color: theme.palette.error.light,
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: '0.75rem',
  },
}));

interface UserRow {
  ref: string;
  name: string;
  email: string;
  picture?: string;
  country?: string;
}

/** Converts "user:default/joel-harley" → "/catalog/default/user/joel-harley" */
function entityRefToPath(ref: string): string {
  const [kindPart, namespacedName] = ref.split(':');
  const [namespace, name] = (namespacedName ?? '').split('/');
  return `/catalog/${namespace ?? 'default'}/${kindPart ?? 'user'}/${name ?? ''}`;
}

export interface MembersPickerProps {
  value: string[]; // user entity refs, e.g. ["user:default/joel-harley"]
  onChange: (refs: string[]) => void;
  /** Refs to hide from the list and exclude from search (e.g. 4-in-the-box leadership) */
  excludeRefs?: string[];
  /**
   * Optional per-member action rendered before the remove button — lets a
   * parent (e.g. TeamEditForm's primary-team star, PAT-678) inject a
   * team-specific control without MembersPicker needing to know about it.
   */
  renderMemberAction?: (ref: string) => ReactNode;
  /**
   * Fired only when a member is added (not on remove) — distinct from the
   * generic onChange, which fires for both. Lets a parent react precisely to
   * "a new member just joined" (e.g. TeamEditForm's primary-team prompt,
   * PAT-779) without diffing `value` itself.
   */
  onMemberAdded?: (ref: string) => void;
  /**
   * Fired only when a member is removed (not on add) — the mirror of
   * onMemberAdded. Lets a parent react precisely to "this member just left"
   * (e.g. TeamEditForm's removal-from-primary reassignment prompt).
   */
  onMemberRemoved?: (ref: string) => void;
  /**
   * A member to draw attention to: its row is highlighted and scrolled into
   * view and focused (e.g. a member pre-added from a deep link, unsaved).
   */
  highlightRef?: string;
}

export function MembersPicker({
  value,
  onChange,
  excludeRefs = [],
  renderMemberAction,
  onMemberAdded,
  onMemberRemoved,
  highlightRef,
}: MembersPickerProps) {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [resolvedMembers, setResolvedMembers] = useState<Map<string, UserRow>>(
    new Map(),
  );
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  // Load all users once for search + resolving existing members
  useEffect(() => {
    setLoading(true);
    catalogApi
      .getEntities({
        filter: { kind: 'User' },
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.annotations',
          'spec.profile',
        ],
      })
      .then(res => {
        const rows: UserRow[] = (res.items as UserEntity[]).map(u => ({
          // Namespace matters — the guest sign-in's identity lives at
          // user:development/guest, not user:default/guest (hardcoded by
          // @backstage/plugin-auth-backend-module-guest-provider regardless
          // of app config). Defaulting away a non-"default" namespace here
          // would silently build a ref to an entity that doesn't exist.
          ref: `user:${u.metadata.namespace ?? 'default'}/${u.metadata.name}`,
          name: u.spec?.profile?.displayName ?? u.metadata.name,
          email: u.spec?.profile?.email ?? '',
          picture: u.spec?.profile?.picture,
          country: u.metadata.annotations?.['user/country'],
        }));
        setAllUsers(rows);

        // Resolve currently selected refs to display info
        const map = new Map<string, UserRow>();
        for (const row of rows) {
          map.set(row.ref, row);
        }
        setResolvedMembers(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [catalogApi]);

  const visibleMembers = value.filter(ref => !excludeRefs.includes(ref));

  const highlightedRow = useRef<HTMLDivElement>(null);
  const highlightVisible = Boolean(
    highlightRef && visibleMembers.includes(highlightRef),
  );
  useEffect(() => {
    if (!highlightVisible) return;
    highlightedRow.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
    });
    highlightedRow.current?.focus();
  }, [highlightVisible]);

  const searchResults =
    query.trim().length >= 2
      ? allUsers
          .filter(u => {
            const q = query.toLowerCase();
            return (
              (u.name.toLowerCase().includes(q) ||
                u.email.toLowerCase().includes(q)) &&
              !value.includes(u.ref)
            );
          })
          .slice(0, 8)
      : [];

  const handleAdd = (user: UserRow) => {
    onChange([...value, user.ref]);
    onMemberAdded?.(user.ref);
    setQuery('');
    setOpen(false);
    setAddingMember(false);
  };

  const handleRemove = (ref: string) => {
    onChange(value.filter(r => r !== ref));
    onMemberRemoved?.(ref);
  };

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="subtitle2">Team Members</Typography>
        <Typography variant="caption" color="textSecondary">
          ({visibleMembers.length} member
          {visibleMembers.length !== 1 ? 's' : ''})
        </Typography>
        {loading && <CircularProgress size={12} />}
      </Box>
      <Typography variant="caption" color="textSecondary" gutterBottom>
        Non-developer team members can be assigned a special role. Using "star",
        you can assign this team as primary for this member.
      </Typography>

      {/* Current members list — leadership excluded */}
      {visibleMembers.length > 0 && (
        <List className={classes.memberList} dense>
          {visibleMembers.map((ref, idx) => {
            const user = resolvedMembers.get(ref);
            const displayName = user?.name ?? ref.split('/').pop() ?? ref;
            const email = user?.email ?? '';
            const picture = user?.picture;
            const country = user?.country;
            return (
              <Box
                key={ref}
                {...(ref === highlightRef && {
                  ref: highlightedRow,
                  tabIndex: -1,
                  'data-highlighted': 'true',
                  style: { backgroundColor: 'rgba(33, 150, 243, 0.12)' },
                })}
              >
                {idx > 0 && <Divider component="li" />}
                <ListItem className={classes.memberItem}>
                  <ListItemAvatar>
                    <Avatar src={picture} className={classes.avatar}>
                      {displayName.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Link to={entityRefToPath(ref)}>
                        {displayName}
                        {country && (
                          <span style={{ marginLeft: 6 }} title={country}>
                            {countryCodeToFlag(country)}
                          </span>
                        )}
                      </Link>
                    }
                    secondary={email}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ className: classes.secondary }}
                  />
                  <ListItemSecondaryAction>
                    {renderMemberAction?.(ref)}
                    <Tooltip title="Remove member">
                      <IconButton
                        size="small"
                        className={classes.removeBtn}
                        onClick={() => handleRemove(ref)}
                        aria-label={`Remove ${displayName}`}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            );
          })}
        </List>
      )}

      {visibleMembers.length === 0 && (
        <Typography className={classes.emptyState}>
          No members yet. Use the search below to add people.
        </Typography>
      )}

      {/* Add member — hidden until user clicks "+ Add member" */}
      {!addingMember ? (
        <Button
          startIcon={<AddIcon />}
          size="small"
          variant="text"
          color="primary"
          onClick={() => setAddingMember(true)}
        >
          Add member
        </Button>
      ) : (
        <Box className={classes.searchBox}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search by name or email…"
            value={query}

            onChange={e => {
              setQuery(e.target.value);
              setOpen(e.target.value.trim().length >= 2);
            }}
            onBlur={() =>
              setTimeout(() => {
                setOpen(false);
                if (!query.trim()) setAddingMember(false);
              }, 150)
            }
            onFocus={() => {
              if (query.trim().length >= 2) setOpen(true);
            }}
          />
          {open && searchResults.length > 0 && (
            <Paper className={classes.dropdown} elevation={4}>
              <List dense>
                {searchResults.map(user => (
                  <ListItem
                    key={user.ref}
                    className={classes.dropdownItem}
                    onClick={() => handleAdd(user)}
                  >
                    <ListItemAvatar>
                      <Avatar src={user.picture} className={classes.avatar}>
                        {user.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.name}
                      secondary={user.email}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{
                        className: classes.secondary,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}
